/**
 * Catálogo de permisos que el panel entiende.
 *
 * IMPORTANTE: esto NO es la lista de lo que el usuario puede hacer — esa la
 * devuelve `/auth/me` y la decide el backend. Esto es sólo el vocabulario, en
 * un único archivo, para que un cambio de nomenclatura en el backend se ajuste
 * en un sitio y no en cuarenta plantillas.
 *
 * La comprobación acepta comodines: `*`, `bots:*` y `platform:*`.
 */

export const PERMISSIONS = Object.freeze({
  // Superficie del cliente
  //
  // El inicio se protege con `metrics:read`: no existe un `dashboard:read` en el
  // contrato, y exigir un permiso que el backend nunca emite deja la página
  // principal inaccesible para todo el mundo.
  METRICS_READ: 'metrics:read',

  CHANNELS_READ: 'channels:read',
  CHANNELS_CONNECT: 'channels:connect',
  CHANNELS_DISCONNECT: 'channels:disconnect',

  BOTS_READ: 'bots:read',
  BOTS_WRITE: 'bots:write',
  /**
   * Cambiar QUÉ MODELO atiende cada tarea del agente.
   *
   * Es un permiso propio y no un `bots:write` más porque hoy el backend reserva
   * el enrutado a plataforma: `models` está en `BOT_RESERVED_FIELDS`
   * (packages/api/src/schemas/tenant.schema.js) y `PATCH /bots/{id}` responde
   * **403** en cuanto ese campo aparece en el cuerpo con un token de ámbito de
   * organización — incluido el de una suplantación.
   *
   * El panel no adivina esa regla ni la copia: pregunta por un permiso. Mientras
   * el backend no lo emita, la sección enseña el enrutado en sólo lectura y dice
   * quién puede cambiarlo; el día que lo emita, el selector aparece solo. Es el
   * mismo criterio que ya se aplicó a `platform:operations:write`: el botón
   * existe para quien el backend autoriza, no para quien el panel suponga.
   */
  BOTS_MODELS_WRITE: 'bots:models:write',

  DATA_READ: 'data:read',
  DATA_WRITE: 'data:write',

  CONVERSATIONS_READ: 'conversations:read',
  CONVERSATIONS_TAKEOVER: 'conversations:takeover',
  CONVERSATIONS_WRITE: 'conversations:write',

  EXPORTS_READ: 'exports:read',
  EXPORTS_WRITE: 'exports:write',

  ORGANIZATION_READ: 'organization:read',
  ORGANIZATION_WRITE: 'organization:write',
  ORGANIZATION_USERS: 'organization:users',

  // Consola de plataforma
  PLATFORM_ORGANIZATIONS_READ: 'platform:organizations:read',
  PLATFORM_ORGANIZATIONS_WRITE: 'platform:organizations:write',
  PLATFORM_IMPERSONATE: 'platform:impersonate',
  PLATFORM_PROMPTS_READ: 'platform:prompts:read',
  PLATFORM_PROMPTS_WRITE: 'platform:prompts:write',
  PLATFORM_MODELS_READ: 'platform:models:read',
  PLATFORM_MODELS_WRITE: 'platform:models:write',
  PLATFORM_PLANS_READ: 'platform:plans:read',
  PLATFORM_PLANS_WRITE: 'platform:plans:write',
  PLATFORM_USAGE_READ: 'platform:usage:read',
  PLATFORM_OPERATIONS_READ: 'platform:operations:read',
  /**
   * Actuar sobre la operación, no sólo mirarla: reencolar un correo del sistema
   * manda un mensaje real al buzón de una persona. El backend ya lo emite
   * (`platform_admin` y `platform_owner` lo tienen; `platform_support` no), así
   * que el botón de reenviar existe exactamente para quien el backend autoriza
   * — no para quien el panel suponga por su rol.
   */
  PLATFORM_OPERATIONS_WRITE: 'platform:operations:write',
  PLATFORM_AUDIT_READ: 'platform:audit:read',
})

/** Ámbitos que devuelve `/auth/me` en el campo `scope`. */
export const SCOPES = Object.freeze({
  ORGANIZATION: 'organization',
  PLATFORM: 'platform',
})

/**
 * ¿La lista concedida cubre el permiso pedido?
 * @param {string[]} granted
 * @param {string} required
 */
export function grants(granted, required) {
  if (!required) return true
  if (!Array.isArray(granted) || granted.length === 0) return false
  if (granted.includes('*') || granted.includes(required)) return true

  // `platform:models:*` cubre `platform:models:read`; `bots:*` cubre `bots:write`.
  const segments = required.split(':')
  for (let i = segments.length - 1; i > 0; i -= 1) {
    if (granted.includes(`${segments.slice(0, i).join(':')}:*`)) return true
  }
  return false
}

/**
 * @param {string[]} granted
 * @param {string[]} required
 * @param {'any'|'all'} mode
 */
export function grantsAny(granted, required = [], mode = 'any') {
  if (required.length === 0) return true
  return mode === 'all'
    ? required.every((permission) => grants(granted, permission))
    : required.some((permission) => grants(granted, permission))
}
